import { useState } from 'react';
import { supabase } from '@/lib/supabaseClient';

export interface S3UploadResult {
  success: boolean;
  url: string;
  key: string;
  fileName: string;
}

export interface UseS3UploadOptions {
  /** S3キーのプレフィックスとなるプロジェクトID（省略時はデフォルト値を使用） */
  projectId?: string;
}

export interface UseS3UploadReturn {
  upload: (file: File, fileName?: string) => Promise<S3UploadResult>;
  uploading: boolean;
  error: string | null;
  reset: () => void;
}

const DEFAULT_PROJECT_ID = '8a4f04ed-3960-472b-978d-5363f01a3123';

/**
 * S3へのファイルアップロードフック
 * アップロード先: https://media.genaiweb.ai/{projectId}/
 *
 * - Body: バイナリ（ArrayBuffer）として送信
 * - x-project-id ヘッダー: プロジェクトID
 * - x-file-name ヘッダー: ファイル名（省略時はEdge Function側で自動生成）
 * - レスポンス URL: https://media.genaiweb.ai/{projectId}/{fileName}
 */
export function useS3Upload(options?: UseS3UploadOptions): UseS3UploadReturn {
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const upload = async (file: File, fileName?: string): Promise<S3UploadResult> => {
    setUploading(true);
    setError(null);

    try {
      const projectId = options?.projectId ?? DEFAULT_PROJECT_ID;

      const supabaseUrl = import.meta.env.VITE_PUBLIC_SUPABASE_URL as string;
      const supabaseAnonKey = import.meta.env.VITE_PUBLIC_SUPABASE_ANON_KEY as string;

      // 認証セッションのトークンを取得
      const { data: sessionData } = await supabase.auth.getSession();
      const accessToken = sessionData?.session?.access_token;

      const headers: Record<string, string> = {
        apikey: supabaseAnonKey,
        Authorization: accessToken ? `Bearer ${accessToken}` : `Bearer ${supabaseAnonKey}`,
        // プロジェクトIDをヘッダーで渡す
        'x-project-id': projectId,
        // Content-Typeをファイルのタイプに合わせる
        'Content-Type': file.type || 'application/octet-stream',
      };

      // ファイル名が指定されている場合はヘッダーで渡す
      if (fileName) {
        headers['x-file-name'] = fileName;
      }

      // BodyはバイナリのArrayBufferとして送信
      const fileBuffer = await file.arrayBuffer();

      const response = await fetch(`${supabaseUrl}/functions/v1/s3-upload`, {
        method: 'POST',
        headers,
        body: fileBuffer,
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data?.error || `HTTPエラー: ${response.status}`);
      }

      if (!data?.success) {
        throw new Error(data?.error || 'アップロードに失敗しました');
      }

      return data as S3UploadResult;
    } catch (err) {
      const message = err instanceof Error ? err.message : 'アップロードに失敗しました';
      setError(message);
      throw err;
    } finally {
      setUploading(false);
    }
  };

  const reset = () => {
    setError(null);
  };

  return { upload, uploading, error, reset };
}